<template>
    <div
        v-timeout="{
            timeoutCallback: signOut,
            time: autoLogoutTime,
            setNotification: setAutoLogoutNotification,
        }"
        :dragging="dragging"
        :dropAllowing="dropAllowing"
        @drop.prevent="dragleave"
        @dragover.prevent="dragover"
        @dragenter.prevent="dragenter"
        @dragleave.prevent="dragleave"
    >
        <Confirmation />
        <Popup />
        <div
            v-if="userType === USER_TYPES.SUPER_USER"
            v-ping="{ timeoutCallback: signOut, setNotification: setPingLogoutNotification }"
        ></div>

        <Navigation @printPage="printPage" />

        <div :class="['container-fluid', 'fixed-top', { 'long-block': navCollapsed }]">
            <Loader :visible="isLoading" />
            <ProgressBar />

            <HeaderView />

            <div class="tabbable">
                <div v-show="isLoaded" id="router" class="tab-content"><RouterView /></div>
            </div>

            <div class="options-right">
                <a
                    :class="['btn', 'btn-question', { 'btn-alone': !isPrintShown() }]"
                    @click.stop="setHelpPage({ help: $route.meta.help, userType, reportType })"
                >
                    <i class="icon-question-icon"></i>
                </a>
                <a v-show="isPrintShown()" class="btn btn-print" @click="printPage">
                    <i class="icon-print"></i>
                </a>
            </div>
        </div>

        <Help :current-page="helpPage" />
    </div>
</template>

<script type="text/babel">
import Loader from './components/common/Loader.vue';
import ProgressBar from './components/common/ProgressBar.vue';
import Navigation from './components/common/Navigation.vue';
import Help from './components/common/Help.vue';
import Confirmation from './components/common/Confirmation.vue';
import Popup from './components/common/Popup.vue';
import HeaderView from './components/common/HeaderView';
import { USER_TYPES } from './constants';
import { mapState, mapMutations, mapActions } from 'vuex';

export default {
    name: 'Layout',

    components: {
        Confirmation,
        HeaderView,
        Help,
        Loader,
        Navigation,
        Popup,
        ProgressBar,
    },

    constants: {
        USER_TYPES,
    },

    data() {
        return {
            pagesWithoutPrint: ['reports'],
        };
    },

    computed: {
        ...mapState({
            navCollapsed: state => state.navCollapsed,
            isLoading: state => state.isLoading,
            reportType: state => state.selectedReportType,
            helpPage: state => state.navigation.helpPage,
            userType: state => state.user.user.type,
            autoLogoutTime: state => state.mariTimeout,
            isLoaded: state => state.isLoaded,
            onKeyDownHandlers: state => state.onKeyDownHandlers,
            dragging: state => state.dragging,
            dropAllowing: state => state.dropAllowing,
        }),
    },

    created() {
        document.onkeydown = event => {
            event = event || window.event;
            this.onKeyDownHandlers.some(({ handler }) => handler(event));
        };
    },

    beforeDestroy() {
        document.onkeydown = null;
    },

    methods: {
        ...mapMutations({
            setUnsavedChanges: 'SET_UNSAVED_CHANGES',
            setNotificationMessage: 'user/SET_NOTIFICATION',
            showLoading: 'SHOW_LOADING',
        }),

        ...mapActions({
            setHelpPage: 'navigation/setHelpPage',
            logout: 'user/logout',
            closeConfirm: 'confirm/hide',
            setTimeout: 'setTimeout',
            startDragging: 'startDragging',
            stopDragging: 'stopDragging',
        }),

        isPrintShown() {
            return !this.pagesWithoutPrint.some(page => this.$route.path.includes(page));
        },

        printPage() {
            window.print();
        },

        setAutoLogoutNotification() {
            this.setNotificationMessage({
                title: this.$t('login.autoLogoutTitle'),
                message: this.$t('login.autoLogoutMessage', { minutes: this.autoLogoutTime }),
            });
        },

        setPingLogoutNotification() {
            this.setNotificationMessage({
                title: this.$t('login.pingLogoutTitle'),
                message: this.$t('login.pingLogoutMessage'),
            });
        },

        signOut(setNotification) {
            this.closeConfirm();
            this.setUnsavedChanges(false);
            this.showLoading(false);
            this.logout();
            this.$nextTick(() => {
                setNotification();
            });
        },

        dragenter(e) {
            let dropEffect = this.dropAllowing ? 'move' : 'none';
            e.dataTransfer.dropEffect = dropEffect;

            if (e.dataTransfer.types.includes('Files')) {
                this.startDragging();
            }
        },

        dragleave(e) {
            if (!e.relatedTarget) {
                this.stopDragging();
            }
        },

        dragover(e) {
            let dropEffect = this.dropAllowing ? 'move' : 'none';
            e.dataTransfer.dropEffect = dropEffect;
        },
    },
};
</script>
